import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import lodash from 'lodash';
import { connect } from 'react-redux';
import { Close } from '@material-ui/icons';
import Tooltip from '@material-ui/core/Tooltip';
import { withStyles } from '@material-ui/core/styles';
import styles from './UserBulkActionList.scss';
import icon from 'styles/icons/binder-icon.scss';
import DefaultMaterialTable from 'common/util/materialTable';
import { Button, BUTTON } from 'components/common/Button/Button';
import { Conditional } from 'common/components/layout';
import { options, theme, columns } from './UserBulkActionListConfig';
import {
    loadUserBinders,
    loadUserStudies,
    resetUserBinders,
    resetUserStudies
} from 'part11/actions/users';
import { addUserToBinders, setShowModal } from 'part11/actions/userBulkAdd';
import AddUserToBinderModal from './AddUserToBinderModal/AddUserToBinderModal';
import AddUserToBinderContainer from './AddUserToBinderModal/AddUserToBinderContainer';
import ConfirmChangeDialog from 'complionAdmin/components/GroupPermissionsSelector/ConfirmChangeDialog';
import { loadHomePageV4Access } from '../../../../common/features/predicates';

export const UserBulkActionList = props => {
    const [saved, setSaved] = useState(false);
    const [binders, setBinders] = useState(props.binders);

    useEffect(() => {
        const { tableName, institutionId, userId, loadUserBinders, loadUserStudies } = props;

        if (tableName === 'Binders') {
            loadUserBinders(institutionId, userId);
        } else {
            loadUserStudies(institutionId, userId);
        }

        return function cleanup() {
            const { tableName, resetUserBinders, resetUserStudies } = props;

            if (tableName === 'Binders') {
                resetUserBinders();
            } else {
                resetUserStudies();
            }
        };
    }, []);

    useEffect(() => {
        setBinders(props.binders);
    }, [props.binders, ...props.binders.map(binder => binder.removed)]);

    function openModal() {
        setSaved(false);
        props.setShowModal(true);
    }

    function handleOnSave() {
        if (saved) {
            const { user, selectedBinderIds, institutionId, addUserToBinders } = props;
            addUserToBinders(institutionId, user.id, selectedBinderIds);

            setSaved(false);
        } else {
            setSaved(true);
        }
    }

    function handleCloseModal() {
        setSaved(false);
        props.setShowModal(false);
    }

    function handleRemove(rowData) {
        const binderId = lodash.get(rowData, 'data.id', undefined);

        const { user, institutionId, removeUserFromBinder } = props;

        const bindersWithRemoved = binders.map(binder => {
            if (binder.id === binderId) {
                binder.removed = true;
            }
            return binder;
        });

        setBinders({ binders: bindersWithRemoved });
        removeUserFromBinder(binderId, user, institutionId);
    }

    function handleUndo(rowData, undoId) {
        const binderId = lodash.get(rowData, 'data.id', undefined);

        const { user, institutionId, undoRemoveUserFromBinder } = props;

        const bindersWithRemovedUndone = binders.map(binder => {
            if (binder.id === binderId) {
                binder.removed = false;
            }
            return binder;
        });

        setBinders({ binders: bindersWithRemovedUndone });
        undoRemoveUserFromBinder(binderId, user, institutionId, undoId);
    }

    const BinderToolTip = withStyles({
        tooltip: {
            display: 'inline-block',
            opacity: '0.85',
            color: 'white',
            backgroundColor: '#28282E',
            padding: '5px',
            fontSize: 14,
        }
    })(Tooltip);

    const TableRow = ({ tableName, rowData, displayName, handleRemove, handleUndo }) => {
        const pi = lodash.get(rowData, 'data.principalInvestigator', undefined);
        const name = lodash.get(rowData, 'data.name', undefined);
        const site = lodash.get(rowData, 'data.site', undefined);
        const removed = lodash.get(rowData, 'data.removed', undefined);
        const undoId = lodash.get(rowData, 'data.undoId', undefined);

        if (tableName === 'Studies') {
            return (
                <tr className={styles.MuiTableRowCustom} data-cy="UserBulkActionList">
                    <td className={styles.MuiTableCellCustom} data-cy="UserBulkActionList-name">{name}</td>
                    <td className={styles.MuiTableCellCustom} data-cy="UserBulkActionList-pi">{pi}</td>
                    <td className={styles.MuiTableCellCustom} data-cy="UserBulkActionList-site">{site}</td>
                </tr>
            );
        }

        if (removed) {
            return (
                <tr className={styles.MuiTableRowCustom} data-cy="UserBulkActionList-tr">
                    <td colSpan={3} className={styles.MuiTableCellRemoved}>
                        <span className={styles.removedBinderUserDisplayName} data-cy="UserBulkActionList-displayName">{displayName}</span>
                        {' has been removed from '}
                        <span className={styles.removedBinderName} data-cy="UserBulkActionList-name">{name}.</span>
                    </td>
                    <td className={styles.MuiTableCellRemovedIcon}>
                        <BinderToolTip title='Restore Binder' enterDelay={250} placement='left-start' >
                            <span className={undoId ? styles.undo : styles.disabledUndo}
                                data-cy="UserBulkActionList-handleUndo"
                                onClick={() => handleUndo(rowData, undoId)}>{' undo'}
                            </span>

                        </BinderToolTip>
                    </td>
                </tr >
            );
        }

        return (
            <tr className={styles.MuiTableRowCustom} data-cy="UserBulkActionList-return">
                <td className={styles.MuiTableCellCustom} data-cy="UserBulkActionList-names">{name}</td>
                <td className={styles.MuiTableCellCustom} data-cy="UserBulkActionList-pis">{pi}</td>
                <td className={styles.MuiTableCellCustom} data-cy="UserBulkActionList-sites">{site}</td>
                <td className={styles.MuiTableCellCustom}>
                    <BinderToolTip title='Remove Binder' enterDelay={250} placement='left-start' >
                        <span className={styles.close} data-cy="UserBulkActionList-span">
                            <Close onClick={() => handleRemove(rowData)} />
                        </span>
                    </BinderToolTip>
                </td>
            </tr>
        );
    };

    const { tableName, user, selectedBinderIds, studies, complion4FlagEnabled } = props;
    const numBindersToAdd = selectedBinderIds.length;
    const tableData = (tableName === 'Binders') ? binders : studies;

    return (
        <div className={styles.container} data-cy="UserBulkActionList-returns">
            <header className={styles.header}>
                <span className={icon.binderIcon}></span>
                <span className={styles.title} data-cy="UserBulkActionList-tableName">{complion4FlagEnabled ? tableName == 'Binders' ? '3.0 Binders' : 'Studies' : tableName}</span>
                <Conditional condition={tableName === 'Binders'}>
                    <div className={styles.buttonContainer}>
                        <Button
                            bgcolor={BUTTON.BGCOLOR.BLUE}
                            color={BUTTON.COLOR.WHITE}
                            onClick={openModal}
                            data-cy="UserBulkActionList-openModal"
                        >
                            Add User to Binders
                        </Button>
                    </div>
                </Conditional>
            </header>
            <section className={styles.listWrapper} data-cy="UserBulkActionList-section">
                <DefaultMaterialTable
                    options={options(tableData.length)}
                    theme={theme}
                    columns={columns}
                    data={tableData}
                    components={{
                        Row: props => {
                            return (
                                <TableRow
                                    tableName={tableName}
                                    rowData={props}
                                    displayName={user.displayName}
                                    handleRemove={handleRemove}
                                    handleUndo={handleUndo}
                                />
                            );
                        },
                    }}
                />
            </section>
            <Conditional condition={tableName === 'Binders'}>
                <AddUserToBinderModal
                    isOpen={props.showModal}
                    onClose={handleCloseModal}
                    isDisabled={!props.selectedBinderIds.length || props.isAddUserToBinderLoading}
                    onSave={handleOnSave}
                    title={`Add ${user.displayName} to Binders`}
                    onCancelText={'Cancel'}
                    onSaveText={'Save'}
                    bodyContainer={<AddUserToBinderContainer user={user} />}
                />
            </Conditional>
            <ConfirmChangeDialog
                open={saved}
                onCancel={() => setSaved(false)}
                onConfirm={handleOnSave}
                title={'Add User to Binders Confirmation'}
                content={`You are about to add ${user.displayName} to ${numBindersToAdd} binders. Do you want to proceed?`}
                confirmText="Yes"
                cancelText="No"
            />
        </div>
    );
};

UserBulkActionList.propTypes = {
    userId: PropTypes.string.isRequired,
    tableName: PropTypes.oneOf(['Binders', 'Studies']),
};

export default connect((state, props) => {
    const userItemObjectName = props.tableName === 'Studies' ? 'userStudies' : 'userBinders';
    const userItemData = lodash.get(state, userItemObjectName, {});
    const isLoading = lodash.get(userItemData, 'isLoading', false);
    const binders = lodash.get(state, 'userBinders.existingData', []);
    const studies = lodash.get(state, 'userStudies.existingData', []);
    const selectedBinderIds = lodash.get(userItemData, 'selectedBinderIds', []);
    const isAddUserToBinderLoading = lodash.get(state, 'addUserToBinders.isLoading', false);
    const showModal = lodash.get(userItemData, 'showModal', false);

    return {
        binders,
        studies,
        isLoading,
        selectedBinderIds,
        isAddUserToBinderLoading,
        showModal,
        complion4FlagEnabled: loadHomePageV4Access(state),
    };
}, {
    loadUserBinders,
    loadUserStudies,
    resetUserBinders,
    resetUserStudies,
    addUserToBinders,
    setShowModal
})(UserBulkActionList);
